import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { featureFlags, isDevToolsEnabled } from '../config/featureFlags';
import { DevFlagToggle } from '../components/DevFlagToggle/DevFlagToggle';
import abInBevLogo from '../assets/images/ab-inbev-footer.svg';
import alocLogoDark from '../assets/images/alloc-dark-theme.png';
import alocLogoLight from '../assets/images/alloc-light-theme.png';
import {
  HomeIcon,
  VerticalIcon,
  StructureIcon,
  TribeIcon,
  ProductIcon,
  SquadIcon,
  PeopleIcon,
  PositionIcon,
  CapacityIcon,
  SchedulerIcon,
  SunIcon,
  MoonIcon,
  ChevronRightIcon,
} from '../assets/icons';
import { ClbIcon } from '@celebration/react';
import styles from './MainLayout.module.scss';

const navIcons: Record<string, React.ReactNode> = {
  home: <HomeIcon />,
  vertical: <VerticalIcon />,
  structure: <StructureIcon />,
  tribe: <TribeIcon />,
  product: <ProductIcon />,
  squad: <SquadIcon />,
  people: <PeopleIcon />,
  position: <PositionIcon />,
  capacity: <CapacityIcon />,
  scheduler: <SchedulerIcon />,
  permissions: <ClbIcon icon="lock-1" size="md" />,
  admin: <ClbIcon icon="settings" size="md" />,
};

const navItems = [
  { to: '/', label: 'Dashboard', icon: 'home', page: 'dashboard' },
  { to: '/vertical', label: 'Vertical', icon: 'vertical', page: 'vertical' },
  { to: '/tower', label: 'Tower', icon: 'structure', page: 'structure' },
  { to: '/tribe', label: 'Tribe', icon: 'tribe', page: 'valueStream' },
  { to: '/product', label: 'Product', icon: 'product', page: 'product' },
  { to: '/squad', label: 'Squad', icon: 'squad', page: 'squad' },
  { to: '/person', label: 'People', icon: 'people', page: 'people' },
  { to: '/position', label: 'Position', icon: 'position', page: 'position' },
  { to: '/permissions', label: 'Permissions', icon: 'permissions', page: 'permissions' },
  { to: '/admin', label: 'Admin', icon: 'admin', page: 'admin' },
  // { to: '/capacity', label: 'Capacity', icon: 'capacity', page: 'capacity' },
  // { to: '/scheduler', label: 'Scheduler', icon: 'scheduler', page: 'scheduler' },
];

const breadcrumbMap: Record<string, string> = {
  '/': 'Dashboard',
  '/vertical': 'Vertical Management',
  '/vertical/add': 'Add Vertical',
  '/tower': 'Tower Management',
  '/tower/add': 'Add Tower',
  '/tribe': 'Tribe Management',
  '/product': 'Product Management',
  '/product/add': 'Add Product',
  '/squad': 'Squad Management',
  '/person': 'People Management',
  '/position': 'Position Management',
  '/position/overview': 'Position Overview',
  '/position/add': 'Add Position',
  '/capacity': 'Capacity Management',
  '/scheduler': 'Scheduler Management',
  '/scheduler/history': 'History',
  '/scheduler/add': 'Add Schedule',
  '/person/add': 'Add Person',
  '/permissions': 'Permission Management',
  '/permissions/add': 'Add Role',
  '/validation-cycles': 'Validation Cycles',
  '/validation-cycles/audit': 'Audit Log',
  '/admin': 'Admin Panel',
  '/admin/role-management': 'Role Management',
  '/admin/role-management/add': 'Add Role/Group',
  '/admin/company-management': 'Company Management',
  '/admin/company-management/add': 'Add Company',
  '/admin/funded-by-management': 'Funded By Management',
  '/admin/funded-by-management/add': 'Add Funded By',
  '/forbidden': 'Access Forbidden',
};

function buildBreadcrumbs(pathname: string, dynamicLabels: Record<string, string> = {}) {
  const crumbs: { label: string; path: string; clickable?: boolean }[] = [{ label: 'Home', path: '/' }];

  if (pathname === '/') return []; // Don't show breadcrumb on Dashboard

  const segments = pathname.split('/').filter(Boolean);
  let currentPath = '';

  // Special handling for /position/:id/edit
  const isPositionEdit = segments[0] === 'position' && segments.length === 3 && segments[2] === 'edit';
  // /validation-cycles/audit has no standalone /validation-cycles route
  const isValidationCycleAudit = segments[0] === 'validation-cycles' && segments[1] === 'audit';

  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;

    // Inject "Position Overview" after "Position Management" in edit flow
    if (isPositionEdit && i === 0) {
      crumbs.push({ label: 'Position Management', path: '/position' });
      crumbs.push({ label: 'Position Overview', path: '/position/overview' });
      continue;
    }

    // Priority: dynamic labels > breadcrumbMap > capitalized segment
    const label =
      dynamicLabels[currentPath] ??
      breadcrumbMap[currentPath] ??
      segments[i].charAt(0).toUpperCase() + segments[i].slice(1);

    // Make the ID segment non-clickable in position edit flow
    // Make the /validation-cycles segment non-clickable (no standalone route exists)
    const clickable = !(isPositionEdit && i === 1) && !(isValidationCycleAudit && i === 0);

    crumbs.push({ label, path: currentPath, clickable });
  }

  return crumbs;
}

interface MainLayoutProps {
  children: React.ReactNode;
}

const getInitials = (name: string): string => {
  if (!name) return '??';

  const parts = name.split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0) return '??';

  // Try to get first letter of first and last parts
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // If only one part, get first two letters
  return parts[0].substring(0, 2).toUpperCase();
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, userPermissions, logout } = useAuth();
  const { dynamicLabels } = useBreadcrumb();
  const { canNavigateTo } = usePermissions();
  const breadcrumbs = buildBreadcrumbs(location.pathname, dynamicLabels);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Get user data from AuthContext (from backend /user/permissions response)
  const userName = userPermissions?.name || user?.name || 'User';
  const userInitials = getInitials(userName);

  // Filter navigation items based on user permissions
  const visibleNavItems = useMemo(() => {
    return navItems.filter(item => {
      return canNavigateTo(item.page);
    });
  }, [canNavigateTo]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className={styles.layout}>
      <header className={styles.topBar}>
        <button
          className={styles.topBarBrand}
          onClick={() => navigate('/')}
          type="button"
          aria-label="Go to Dashboard"
        >
          <img src={theme === 'dark' ? alocLogoDark : alocLogoLight} alt="Aloc" className={styles.topBarLogo} />
        </button>
        <div className={styles.topBarActions}>
          {isDevToolsEnabled && <span className={styles.topBarEnv}>DEV</span>}
          <DevFlagToggle />
          <button
            className={styles.themeToggle}
            onClick={toggleTheme}
            type="button"
            aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
            title={theme === 'dark' ? 'Tema claro' : 'Tema escuro'}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
          <div className={styles.userMenu} ref={userMenuRef}>
            <button
              className={styles.userMenuBtn}
              onClick={featureFlags.ssoEnabled ? () => setUserMenuOpen((prev) => !prev) : undefined}
              type="button"
              style={!featureFlags.ssoEnabled ? { cursor: 'default' } : undefined}
            >
              <span className={styles.userAvatar}>{userInitials}</span>
              <span className={styles.userName}>{userName}</span>
              {featureFlags.ssoEnabled && (
                <svg
                  className={`${styles.userArrow} ${userMenuOpen ? styles.userArrowOpen : ''}`}
                  width="13"
                  height="7"
                  viewBox="0 0 13 7"
                  fill="none"
                >
                  <path
                    d="M0.538529 0.309342C0.749324 0.098548 1.07918 0.0793849 1.31162 0.251853L1.37822 0.309342L6.50004 5.4309L11.6219 0.309342C11.8327 0.098548 12.1625 0.0793849 12.395 0.251853L12.4616 0.309342C12.6723 0.520137 12.6915 0.849995 12.519 1.08244L12.4616 1.14903L6.91988 6.6907C6.70909 6.90149 6.37923 6.92066 6.14679 6.74819L6.0802 6.6907L0.538529 1.14903C0.306656 0.917158 0.306656 0.541216 0.538529 0.309342Z"
                    fill="var(--text-tertiary)"
                  />
                </svg>
              )}
            </button>
            {userMenuOpen && featureFlags.ssoEnabled ? (
              <div className={styles.userDropdown}>
                <button
                  className={styles.userDropdownItem}
                  onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                  }}
                  type="button"
                >
                  Sign Out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>
      <div className={styles.body}>
        <aside className={styles.sidebar}>
          <nav className={styles.nav}>
            {visibleNavItems.map((item) => (
              <button
                key={item.to}
                className={`${styles.navItem} ${isActive(item.to) ? styles.active : ''}`}
                onClick={() => navigate(item.to)}
                title={item.label}
              >
                <span className={styles.navIcon}>
                  {navIcons[item.icon]}
                </span>
                <span className={styles.navLabel}>{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>
        <main className={styles.main}>
          <div className={styles.content}>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <ol className={styles.breadcrumbList}>
                {breadcrumbs.map((crumb, idx) => {
                  const isLast = idx === breadcrumbs.length - 1;
                  const isHome = idx === 0;
                  const isClickable = crumb.clickable !== false;
                  return (
                    <li key={crumb.path} className={styles.breadcrumbItem}>
                      {idx > 0 ? (
                        <ChevronRightIcon className={styles.breadcrumbSep} />
                      ) : null}
                      {isLast ? (
                        <span className={styles.breadcrumbActive} aria-current="page">
                          {crumb.label}
                        </span>
                      ) : isClickable ? (
                        <button
                          className={styles.breadcrumbLink}
                          onClick={() => navigate(crumb.path)}
                          type="button"
                        >
                          {isHome ? (
                            <HomeIcon width={14} height={14} />
                          ) : (
                            crumb.label
                          )}
                        </button>
                      ) : (
                        <span className={styles.breadcrumbActive}>
                          {crumb.label}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
            {children}
          </div>
          <footer className={styles.footer}>
            <img src={abInBevLogo} alt="AB InBev" className={styles.footerImage} />
            <span className={styles.footerText}>
              &copy; 2026 Anheuser-Busch InBev All rights reserved. For internal use only.
            </span>
            <span className={styles.footerTagline}>To a Future With More Cheers</span>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
